import {
  formatMinor,
  formatRateBp,
  PROFILE_DIMENSION_LABELS,
  type ProfileDimension,
  type VendorObservation,
} from '@hvac/contracts';
import { Alert, Card, CardBody, CardHeader } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { sessionQuery } from '../../shared/api/queries.js';
import { vendorIntelligenceQuery } from '../../shared/api/intelligence-queries.js';

/**
 * A supplier's memory.
 *
 * This is the page that survives a bookkeeper leaving. Everything on it is a
 * count of confirmed records, how this supplier's invoices have been coded,
 * whether they charge HST, when that changed, so a new person can read in two
 * minutes what previously lived in someone's head.
 */

const DIMENSION_ORDER: readonly ProfileDimension[] = [
  'category',
  'classification',
  'tax_present',
  'job_linked',
  'cca_class',
];

export function VendorMemoryPage() {
  const { vendorId } = useParams({ from: '/protected/suppliers/$vendorId' });
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';

  const memory = useQuery(vendorIntelligenceQuery(organizationId, vendorId));

  if (memory.isPending) {
    return <p className="p-6 text-sm text-content-muted">Loading…</p>;
  }
  if (memory.isError) {
    return (
      <Alert tone="danger" title="That supplier could not be loaded">
        {memory.error instanceof Error ? memory.error.message : 'Unknown error.'}
      </Alert>
    );
  }

  const data = memory.data;
  const taxRecords = (data.taxPresentCount ?? 0) + (data.taxAbsentCount ?? 0);

  const byDimension = new Map<ProfileDimension, VendorObservation[]>();
  for (const observation of data.observations) {
    const existing = byDimension.get(observation.dimension) ?? [];
    existing.push(observation);
    byDimension.set(observation.dimension, existing);
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link
          to="/suppliers/$vendorId"
          params={{ vendorId }}
          className="text-sm text-content-muted hover:text-content"
        >
          ← Supplier
        </Link>
        <h1 className="mt-1 text-xl font-semibold text-content">{data.displayName}</h1>
        <p className="text-sm text-content-muted">
          {data.recordCount === 0
            ? 'No confirmed records yet, so there is nothing to know about this supplier.'
            : `${data.recordCount} confirmed record${data.recordCount === 1 ? '' : 's'}${
                data.firstRecordedOn === null ? '' : ` since ${data.firstRecordedOn}`
              }`}
        </p>
      </div>

      {data.recordCount === 0 ? (
        <Card>
          <CardBody className="py-10 text-center">
            <p className="text-sm text-content-muted">
              Suggestions for this supplier will appear once a few records have been confirmed.
              Until then the engine says nothing, which is the honest answer.
            </p>
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Records" value={String(data.recordCount)} />
            <Stat
              label="Total"
              value={data.totalAmountMinor === null ? '-' : formatMinor(data.totalAmountMinor)}
            />
            <Stat
              label="Typical"
              value={data.meanAmountMinor === null ? '-' : formatMinor(data.meanAmountMinor)}
            />
            <Stat
              label="Usual HST"
              value={formatRateBp(data.meanTaxRateBp)}
              hint={
                taxRecords === 0
                  ? undefined
                  : `charged on ${data.taxPresentCount ?? 0} of ${taxRecords}`
              }
            />
          </div>

          <Card>
            <CardHeader
              title="What has been observed"
              description="Counted from confirmed records. Every number here is checkable against the ledger."
            />
            <CardBody className="flex flex-col gap-5">
              {DIMENSION_ORDER.filter((dimension) => byDimension.has(dimension)).map(
                (dimension) => (
                  <div key={dimension}>
                    <p className="text-sm font-medium text-content">
                      {PROFILE_DIMENSION_LABELS[dimension]}
                    </p>
                    <ul className="mt-1 flex flex-col gap-1">
                      {(byDimension.get(dimension) ?? [])
                        .slice()
                        .sort((a, b) => b.observationCount - a.observationCount)
                        .map((observation) => (
                          <li
                            key={observation.valueKey}
                            className="flex items-baseline justify-between gap-3 text-sm"
                          >
                            <span className="text-content">
                              {observation.label ?? observation.valueKey}
                            </span>
                            <span className="tabular text-xs text-content-muted">
                              {observation.observationCount}×
                              {observation.correctedAwayCount > 0
                                ? ` · corrected away ${observation.correctedAwayCount}×`
                                : ''}
                              {observation.lastObservedOn === null
                                ? ''
                                : ` · last ${observation.lastObservedOn}`}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </div>
                ),
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Year by year"
              description="A supplier that changes what it sells you should be visible, not merely reflected in a confidence score that quietly moved."
            />
            <CardBody className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                    <th className="px-3 py-2 font-semibold">Year</th>
                    <th className="px-3 py-2 font-semibold">Records</th>
                    <th className="px-3 py-2 font-semibold">Mostly</th>
                    <th className="px-3 py-2 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.timeline.map((entry) => (
                    <tr key={entry.periodYear} className="border-b border-border last:border-0">
                      <td className="tabular px-3 py-2">{entry.periodYear}</td>
                      <td className="tabular px-3 py-2">{entry.recordCount}</td>
                      <td className="px-3 py-2">
                        {entry.topCategory ?? '-'}
                        {entry.topCategoryCount === null ? null : (
                          <span className="text-xs text-content-subtle">
                            {' '}
                            ({entry.topCategoryCount}×)
                          </span>
                        )}
                      </td>
                      <td className="tabular px-3 py-2 text-right">
                        {formatMinor(entry.totalAmountMinor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>

          <p className="text-xs text-content-subtle">
            {data.aliasCount} spelling{data.aliasCount === 1 ? '' : 's'} of this supplier’s name
            resolve here automatically.
          </p>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string | undefined }) {
  return (
    <div className="rounded-[var(--radius-surface)] border border-border bg-surface px-3 py-2">
      <p className="text-xs text-content-muted">{label}</p>
      <p className="tabular text-lg font-semibold text-content">{value}</p>
      {hint === undefined ? null : <p className="text-xs text-content-subtle">{hint}</p>}
    </div>
  );
}
